import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, FormHelperText, Grid, Stack, Typography } from '@mui/material';
import { get } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Job } from '../../../../../app/Slices/SevenDayApiTypes';
import { CustomIconButton } from '../../../../../layout/styledcomponents/CustomIconButton';
import { Status } from '../../../../enums/contractor';
import FormCheckbox from '../../../../shared/FormCheckbox';
import { ModuleContractor } from '../../../../types/contractor';
import ContractorPolicyModal from './ContractorPolicyModal';

const ContractorPolicies = ({
    jobs,
    contractors,
    isReservationEditForm = false,
    /** When the parent section already explains context (e.g. DetailsSubsection description). */
    hideIntroCaption = false,
    jobFieldPath,
}: {
    jobs: Job[];
    isReservationEditForm?: boolean;
    hideIntroCaption?: boolean;
    contractors: ModuleContractor[];
    /** RHF path to each job. Defaults to `jobs.{id}` (reservation request record). */
    jobFieldPath?: string;
}) => {
    const {
        control,
        formState: { errors },
        setValue,
    } = useFormContext();

    const activeJobs = jobs.filter(
        (job) => ![Status.Cancelled, Status.Declined].includes(job.status),
    );

    const seenContractorIds = new Set<number>();
    const uniqueContractorJobs = activeJobs.filter((job) => {
        const contractorId = job.contractor?.id;
        const hasPolicy = !!contractors.find(
            (contractor) => contractor.id === contractorId,
        )?.cancellationPolicy;
        if (
            !contractorId ||
            seenContractorIds.has(contractorId) ||
            !hasPolicy
        ) {
            return false;
        }
        seenContractorIds.add(contractorId);
        return true;
    });

    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [selectedContractor, setSelectedContractor] =
        useState<ModuleContractor | null>(null);

    const handleOpenModal = (contractor: ModuleContractor) => {
        setSelectedContractor(contractor);
        setModalOpen(true);
    };

    const getJobPath = (job: Job) =>
        jobFieldPath ? jobFieldPath : (jobFieldPath ?? `jobs.${job.id}`);

    return (
        <Box sx={{ mt: isReservationEditForm ? 0 : 2 }}>
            <Stack>
                {!isReservationEditForm && (
                    <Typography
                        variant='body1'
                        sx={{
                            fontWeight: 'bold',
                        }}
                        gutterBottom
                    >
                        Contractor Policies
                    </Typography>
                )}
                {!hideIntroCaption ? (
                    <Typography variant='caption' fontWeight={550} gutterBottom>
                        Contractors operate independently from experience
                        owners, so you'll need to read and agree to their terms
                        and conditions before making this reservation.
                    </Typography>
                ) : null}
            </Stack>
            <>
                {uniqueContractorJobs.map((job) => {
                    const name = getJobPath(job);
                    const policyError = get(errors, `${name}.policy.checked`);
                    const contractor = contractors.find(
                        (contractor) => contractor.id === job.contractor.id,
                    );
                    const checkedByName = job.policy?.checkedBy
                        ? `${job.policy.checkedBy.firstName} ${job.policy.checkedBy.lastName}`.trim()
                        : '';

                    const existingJobsWithPolicyChecked = activeJobs.find(
                        (existingJob) =>
                            existingJob.contractor?.id === job.contractor?.id &&
                            existingJob?.policy &&
                            existingJob.policy.checkedAt,
                    );

                    if (!contractor) {
                        return null;
                    }

                    const policyLabelId = `contractor-${contractor.id}-cancellation-policy`;

                    return (
                        <Grid
                            key={job.contractor.id}
                            role='group'
                            aria-labelledby={policyLabelId}
                        >
                            <Grid
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    display='flex'
                                    alignItems='center'
                                    sx={{ m: 0 }}
                                >
                                    {
                                        <FormCheckbox
                                            name={`${name}.policy.checked`}
                                            control={control}
                                            sx={{ p: 0, m: 0 }}
                                            disabled={
                                                !!existingJobsWithPolicyChecked
                                            }
                                            onChange={(_, checked) => {
                                                activeJobs.forEach(
                                                    (otherJob) => {
                                                        if (
                                                            otherJob.contractor
                                                                ?.id !==
                                                                job.contractor
                                                                    .id ||
                                                            otherJob.id ===
                                                                job.id
                                                        ) {
                                                            return;
                                                        }

                                                        setValue(
                                                            `${getJobPath(otherJob)}.policy.checked`,
                                                            checked,
                                                            {
                                                                shouldDirty: true,
                                                                shouldValidate: true,
                                                            },
                                                        );
                                                    },
                                                );
                                            }}
                                        />
                                    }
                                    <Typography
                                        id={policyLabelId}
                                        variant='caption'
                                        fontWeight={550}
                                        sx={{
                                            ml: 1,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            handleOpenModal(contractor);
                                        }}
                                    >
                                        {contractor.name}'s Cancellation Policy
                                    </Typography>
                                </Box>

                                <CustomIconButton
                                    variant='contained'
                                    color='primary'
                                    onClick={() => {
                                        handleOpenModal(contractor);
                                    }}
                                >
                                    <KeyboardArrowRightIcon />
                                </CustomIconButton>
                            </Grid>
                            {existingJobsWithPolicyChecked?.policy && (
                                <Typography
                                    color='text.secondary'
                                    sx={{
                                        ml: 4,
                                        display: 'block',
                                        fontSize: '12px',
                                        fontStyle: 'italic',
                                    }}
                                >
                                    Agreed on{' '}
                                    {moment(
                                        existingJobsWithPolicyChecked.policy
                                            .checkedAt,
                                    ).format('D MMM YYYY')}
                                    {checkedByName
                                        ? ` by ${checkedByName}`
                                        : ''}
                                </Typography>
                            )}
                            {policyError && (
                                <FormHelperText error sx={{ m: 0, p: 0 }}>
                                    {policyError.message as string}
                                </FormHelperText>
                            )}
                        </Grid>
                    );
                })}

                {selectedContractor && (
                    <ContractorPolicyModal
                        contractor={selectedContractor}
                        open={modalOpen}
                        onClose={() => setModalOpen(false)}
                    />
                )}
            </>
        </Box>
    );
};

export default ContractorPolicies;
